import React from "react";
import { Card, Form, Select, DatePicker, Button, Row, Col, Space } from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { PERMISSIONS, usePermissions } from "../../context/PermissionContext";

const { Option } = Select;
const { RangePicker } = DatePicker;

export interface FilterValues {
  location_id?: number;
  shift_type?: "morning" | "day" | "night";
  status?: "scheduled" | "completed" | "canceled";
  date_range?: [Dayjs, Dayjs];
  user_ids?: number[];
  user_id?: number; // Add this for API filtering
}

interface ScheduleFilterProps {
  initialValues?: FilterValues;
  onFilter: (values: FilterValues) => void;
  onReset: () => void;
  locations?: Array<{ id: number; name: string }>;
  staff?: Array<{ id: number; name: string }>;
  canSeeAllSchedules?: boolean;
}

const ScheduleFilter: React.FC<ScheduleFilterProps> = ({
  initialValues,
  onFilter,
  onReset,
  locations = [],
  staff = [],
  canSeeAllSchedules = false,
}) => {
  const [form] = Form.useForm();
  const { hasPermission } = usePermissions();
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  const handleReset = () => {
    form.resetFields();
    onReset();
  };

  // Show all staff since filtering is handled by the user_ids filter
  const availableStaff = staff;

  // Disable past dates for better UX
  // const disabledDate = (current: Dayjs) => {
  //   // Disable dates before today
  //   return current && current < dayjs().startOf("day");
  // };

  // Set default date range presets
  const rangePresets: {
    label: string;
    value: [Dayjs, Dayjs];
  }[] = [
    { label: "Today", value: [dayjs(), dayjs()] },
    { label: "Next 7 Days", value: [dayjs(), dayjs().add(6, "day")] },
    { label: "Next 14 Days", value: [dayjs(), dayjs().add(13, "day")] },
    { label: "Next 30 Days", value: [dayjs(), dayjs().add(29, "day")] },
    {
      label: "This Week",
      value: [dayjs().startOf("week"), dayjs().endOf("week")],
    },
    {
      label: "Next Week",
      value: [
        dayjs().add(1, "week").startOf("week"),
        dayjs().add(1, "week").endOf("week"),
      ],
    },
    {
      label: "This Month",
      value: [dayjs().startOf("month"), dayjs().endOf("month")],
    },
    {
      label: "Next Month",
      value: [
        dayjs().add(1, "month").startOf("month"),
        dayjs().add(1, "month").endOf("month"),
      ],
    },
  ];

  return (
    <Card className="mb-4">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={onFilter}
      >
        <Row gutter={16}>
          {canSeeAllLocations && (
            <Col xs={24} sm={12} md={6}>
              <Form.Item name="location_id" label="Location">
                <Select placeholder="Select Location" allowClear>
                  {locations.map((location) => (
                    <Option key={location.id} value={location.id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          <Col xs={24} sm={12} md={6}>
            <Form.Item name="shift_type" label="Shift Type">
              <Select placeholder="Select Shift Type" allowClear>
                <Option value="morning">Morning</Option>
                <Option value="day">Day</Option>
                <Option value="night">Night</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Form.Item name="status" label="Status">
              <Select placeholder="Select Status" allowClear>
                <Option value="scheduled">Scheduled</Option>
                <Option value="completed">Completed</Option>
                <Option value="canceled">Canceled</Option>
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <Form.Item name="date_range" label="Date Range">
              <RangePicker
                className="w-full"
                // disabledDate={disabledDate}
                presets={rangePresets}
                format="MMM DD, YYYY"
                placeholder={["Start Date", "End Date"]}
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Staff selection - always available */}
          {canSeeAllSchedules && (
            <Col xs={24}>
              <Form.Item name="user_ids" label="Staff Members">
                <Select
                  placeholder="Select Staff Members (leave empty to show all)"
                  allowClear
                  mode="multiple"
                  optionFilterProp="children"
                  showSearch
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                >
                  {availableStaff.map((person) => (
                    <Option key={person.id} value={person.id}>
                      {person.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>

        <div className="flex justify-end">
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
            <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
              Apply Filters
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};

export default ScheduleFilter;
